import React, { useMemo, useRef, useState } from 'react';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    Form,
    Icon,
    message,
    Modal,
    Row,
    Select,
    Spin,
    Switch,
    Table,
    Tooltip,
} from 'antd';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { saveAs } from 'file-saver';
import json2csv from 'json2csv';
import moment from 'moment';

import axios from 'util/Api';
import { searchGlobalAssetData } from '../../../appRedux/actions/Common';
import RangeDataChart from '../../DeviceMap/RangeDataChartV2';
import ProcessedDataSummary, { buildProcessedDataSummary } from '../../DeviceMap/ProcessedDataSummary';
import AssetSearch from '../../../components/AssetSearch';
import ImagePreviewModal from '../../../components/ImagePreviewModal';
import chartIcon from 'assets/images/icon/chart.png';
import './style.css';

const { RangePicker } = DatePicker;

const mapStateToProps = ({ auth, commonData }) => ({
    authUser: auth.authUser,
    assetData: commonData.assetSearchData,
    assetSearchLoading: commonData.assetSearchLoading,
});

function mapDispatchToProps(dispatch) {
    return {
        searchGlobalAssetData: (query) => dispatch(searchGlobalAssetData(query)),
    };
}

function Refuel(props) {
    const { getFieldDecorator } = props.form;

    const [reportLoading, setReportLoading] = useState(false);
    const [isDownloadEnabled, setIsDownloadEnabled] = useState(true);
    const [selectionMode, setSelectionMode] = useState(null); // 'location' | 'asset'
    // When off (default), assets without a fuel sensor are excluded from the report.
    const [includeAssetsWithoutFuelSensor, setIncludeAssetsWithoutFuelSensor] = useState(false);

    const [refuelReportData, setRefuelReportData] = useState([]);
    const [chartIconLoading, setChartIconLoading] = useState({});
    const [refuelClickLoading, setRefuelClickLoading] = useState({});

    const [refuelDetailsModalVisible, setRefuelDetailsModalVisible] = useState(false);
    const [selectedRefuelDetails, setSelectedRefuelDetails] = useState(null);
    const [photoPreview, setPhotoPreview] = useState({ visible: false, index: 0 });

    const [refuelEventsModalVisible, setRefuelEventsModalVisible] = useState(false);
    const [selectedRefuelEvents, setSelectedRefuelEvents] = useState([]);

    const [modalKey, setModalKey] = useState(0);
    const [modalVisibleRef, setModalVisibleRef] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [chartDataReady, setChartDataReady] = useState(false);
    const [selectedChartDate, setSelectedChartDate] = useState(null);
    const [selectedChartCacheKey, setSelectedChartCacheKey] = useState(null);
    const [processedInfoModalVisible, setProcessedInfoModalVisible] = useState(false);
    const [processedInfoLoading, setProcessedInfoLoading] = useState(false);
    const [processedInfoIconLoading, setProcessedInfoIconLoading] = useState({});
    const [processedInfoContext, setProcessedInfoContext] = useState(null);

    const assetSelectedRef = useRef(null);
    // Caching strategy:
    // - key: assetId => { deviceId, fuelSensorCalibrationList }
    // - key: `${assetId}-${periodFrom}-${periodTo}` => { refuelData, ...assetMeta }
    const assetDataRef = useRef(new Map());
    // Date-aware GPS/fuel-sensor connectivity, cached per `${assetId}-${dayTimestamp}`.
    const connectivityRef = useRef(new Map());

    // Whether an asset had GPS / fuel sensor installed on a specific day, from the Sub Asset
    // Transfer history (GET /sub-asset-transfer/connectivity/:assetId?date=<ms>).
    const getConnectivityOnDate = async (assetId, dayTimestamp) => {
        const key = `${assetId}-${dayTimestamp}`;
        const cached = connectivityRef.current.get(key);
        if (cached) {
            return cached;
        }
        const response = await axios.get(`/sub-asset-transfer/connectivity/${assetId}?date=${dayTimestamp}`);
        const conn = {
            hasGps: !!(response.data && response.data.hasGps),
            hasFuelSensor: !!(response.data && response.data.hasFuelSensor),
        };
        connectivityRef.current.set(key, conn);
        return conn;
    };

    const hasManualOrDetectedRefuel = (row) => {
        const manualRefuel = Number((row && row.quantity) || 0);
        const detectedRefuel = Number((row && row.refuelDetected) || 0);
        return manualRefuel > 0 || (!isNaN(detectedRefuel) && detectedRefuel > 0);
    };

    const reportRows = useMemo(() => (refuelReportData || []).filter(hasManualOrDetectedRefuel), [refuelReportData]);

    const validateForm = (rule, value, callback) => {
        if (!selectionMode) {
            callback('Please select either Location/Sub-Location or Asset');
            return;
        }

        if (selectionMode === 'location' && (!props.form.getFieldValue('location') || !props.form.getFieldValue('subLocation'))) {
            callback('Both Location and Sub Location are required');
            return;
        }

        if (selectionMode === 'asset' && !props.form.getFieldValue('asset')) {
            callback('Asset is required');
            return;
        }

        if (!value.periodFromTo || value.periodFromTo.length !== 2) {
            callback('Please select a valid period');
            return;
        }

        callback();
    };

    const handleRefuelDetailsClick = (record) => {
        if (!record || !record.ids || !record.ids.length) {
            message.info('No manual refuel entry found for this date');
            return;
        }

        setSelectedRefuelDetails(record.ids);
        setRefuelDetailsModalVisible(true);
    };

    const handleRefuelDetailsModalClose = () => {
        setRefuelDetailsModalVisible(false);
        setSelectedRefuelDetails(null);
        setPhotoPreview({ visible: false, index: 0 });
    };

    // Flat list of every photo across all rows in the refuel details table, each
    // carrying its parent record so the lightbox can show that row's details.
    const allRefuelPhotos = useMemo(() => {
        const list = [];
        (selectedRefuelDetails || []).forEach((record) => {
            if (record && Array.isArray(record.photos)) {
                record.photos.forEach((p) => {
                    if (p && p.url) list.push({ url: p.url, name: p.name, record });
                });
            }
        });
        return list;
    }, [selectedRefuelDetails]);

    const openPhotoPreview = (index) => setPhotoPreview({ visible: true, index });
    const closePhotoPreview = () => setPhotoPreview((prev) => ({ ...prev, visible: false }));

    const handleRefuelEventsModalClose = () => {
        setRefuelEventsModalVisible(false);
        setSelectedRefuelEvents([]);
        setRefuelClickLoading({});
    };

    const getEventsFromRecord = (assetId, date, record) => {
        const directEvents = Array.isArray(record && record.refuelEvents) ? record.refuelEvents : [];
        if (directEvents.length > 0) {
            return directEvents;
        }

        const cacheKey = getFleetApiCacheKey(assetId, date);
        const cachedData = assetDataRef.current.get(cacheKey);
        const cachedRefuelData = cachedData && cachedData.refuelData;
        const cachedEvents = cachedRefuelData && cachedRefuelData.deviceProcessedData && cachedRefuelData.deviceProcessedData.newConsumed
            && Array.isArray(cachedRefuelData.deviceProcessedData.newConsumed.refuel_events)
            ? cachedRefuelData.deviceProcessedData.newConsumed.refuel_events
            : [];
        return cachedEvents;
    };

    const getFleetApiPeriod = (date) => {
        const baseDate = Number.isFinite(Number(date)) ? Number(date) : date;
        return {
            periodFrom: moment(baseDate).startOf('day').valueOf(),
            periodTo: moment(baseDate).endOf('day').valueOf(),
        };
    };

    const getFleetApiCacheKey = (assetId, date) => {
        const { periodFrom, periodTo } = getFleetApiPeriod(date);
        return `${assetId}-${periodFrom}-${periodTo}`;
    };

    const handleRefuelClick = async (asset, record) => {
        if (!asset || !asset._id) {
            message.error('Asset information is missing');
            return;
        }

        const assetId = asset._id.toString();
        const date = record.date;
        const loadingKey = `${assetId}-${moment(date).format('YYYY-MM-DD')}`;

        setRefuelClickLoading(prev => ({ ...prev, [loadingKey]: true }));

        try {
            const events = getEventsFromRecord(assetId, date, record);
            if (events.length > 0) {
                setSelectedRefuelEvents(events.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)));
                setRefuelEventsModalVisible(true);
            } else {
                message.info('No refuel events found for this date');
            }
        } catch (error) {
            console.error('Error processing refuel events:', error);
            message.error('Failed to process refuel events');
        } finally {
            setRefuelClickLoading(prev => ({ ...prev, [loadingKey]: false }));
        }
    };

    const getAssetMeta = async (assetId) => {
        let meta = assetDataRef.current.get(assetId);
        if (meta && meta.metaLoaded) {
            return meta;
        }

        const assetResponse = await axios.get(`/asset/${assetId}`);
        const { data: assetDetails } = assetResponse || {};

        const dependentAssets = assetDetails && Array.isArray(assetDetails.dependents)
            ? assetDetails.dependents
            : [];
        const gpsDeviceAsset = dependentAssets.find(d => d && d.tagDeviceId);
        const sensorDeviceAsset = gpsDeviceAsset && Array.isArray(gpsDeviceAsset.dependents)
            ? gpsDeviceAsset.dependents.find(d => d && (d.fuelSensorId || (d.assetModel && d.assetModel.isFuelSensorDevice)))
            : null;

        let deviceId = null;
        let fuelSensorCalibrationList = [];

        if (gpsDeviceAsset) {
            deviceId = gpsDeviceAsset.tagDeviceId;
            if (sensorDeviceAsset && sensorDeviceAsset.fuelSensorCalibrationList && sensorDeviceAsset.fuelSensorCalibrationList.list) {
                fuelSensorCalibrationList = sensorDeviceAsset.fuelSensorCalibrationList.list;
            }
        }

        meta = {
            ...(assetDetails || {}),
            metaLoaded: true,
            deviceId,
            fuelSensorCalibrationList,
            hasGpsInstalled: !!gpsDeviceAsset,
            hasFuelSensorInstalled: !!sensorDeviceAsset,
        };
        assetDataRef.current.set(assetId, meta);
        return meta;
    };

    const enrichReportRowsWithAssetMeta = async (rows = []) => {
        const uniqueAssetIds = Array.from(new Set(
            rows
                .map((row) => row && row.asset && row.asset._id ? row.asset._id.toString() : null)
                .filter(Boolean)
        ));

        const assetMetaMap = {};

        await Promise.all(uniqueAssetIds.map(async (assetId) => {
            try {
                // Still needed for deviceId + fuelSensorCalibrationList used by the sensor processing.
                assetMetaMap[assetId] = await getAssetMeta(assetId);
            } catch (error) {
                console.error(`Failed to fetch asset meta for asset ${assetId}:`, error);
                assetMetaMap[assetId] = {
                    metaLoaded: true,
                    hasGpsInstalled: false,
                    hasFuelSensorInstalled: false,
                    deviceId: null,
                    fuelSensorCalibrationList: [],
                };
            }
        }));

        // GPS / fuel-sensor "installed" is now date-aware: evaluated per row on that row's own day.
        const rowKeys = rows.map((row) => {
            const assetId = row && row.asset && row.asset._id ? row.asset._id.toString() : null;
            const day = row && row.date ? Number(row.date) : null;
            return { assetId, day, key: (assetId && day) ? `${assetId}-${day}` : null };
        });
        const connectivityMap = {};
        await Promise.all(Array.from(new Set(rowKeys.map((r) => r.key).filter(Boolean))).map(async (key) => {
            const separatorIndex = key.indexOf('-');
            const assetId = key.slice(0, separatorIndex);
            const day = key.slice(separatorIndex + 1);
            try {
                connectivityMap[key] = await getConnectivityOnDate(assetId, Number(day));
            } catch (error) {
                console.error(`Failed to fetch connectivity for ${key}:`, error);
                connectivityMap[key] = { hasGps: false, hasFuelSensor: false };
            }
        }));

        return rows.map((row, index) => {
            const { key } = rowKeys[index];
            const conn = key ? connectivityMap[key] : null;
            return {
                ...row,
                hasGpsInstalled: conn ? !!conn.hasGps : false,
                hasFuelSensorInstalled: conn ? !!conn.hasFuelSensor : false,
            };
        });
    };

    const ensureRefuelDataInCache = async (asset, record) => {
        if (!asset || !asset._id) {
            return { error: 'ASSET_MISSING' };
        }

        const assetId = asset._id.toString();
        const date = record && record.date;
        if (!date) {
            return { error: 'DATE_MISSING' };
        }

        const { periodFrom, periodTo } = getFleetApiPeriod(date);
        const cacheKey = `${assetId}-${periodFrom}-${periodTo}`;

        const cachedData = assetDataRef.current.get(cacheKey);
        const cachedRefuelData = cachedData && cachedData.refuelData;
        const isDataCached = cachedRefuelData
            && cachedRefuelData.startTime === periodFrom
            && cachedRefuelData.endTime === periodTo
            && Array.isArray(cachedRefuelData.deviceData)
            && cachedRefuelData.deviceData.length > 0;

        if (isDataCached) {
            return { cacheKey, data: cachedData };
        }

        const assetMeta = await getAssetMeta(assetId);
        if (!assetMeta || !assetMeta.deviceId) {
            return { error: 'NO_DEVICE', cacheKey };
        }

        const response = await axios.get(`/iot-device-data/in-period/${assetMeta.deviceId}/${periodFrom}/${periodTo}/${assetId}`);
        const { data } = response || {};
        const refuelData = {
            deviceData: (data && data.deviceData) || [],
            deviceProcessedData: (data && data.deviceProcessedData) || {},
            date,
            startTime: periodFrom,
            endTime: periodTo,
        };

        const mergedData = {
            ...assetMeta,
            refuelData,
        };

        assetDataRef.current.set(cacheKey, mergedData);
        return { cacheKey, data: mergedData };
    };

    const handleChartClick = async (asset, record) => {
        if (!asset || !asset._id) {
            message.error('Asset information is missing');
            return;
        }

        const assetId = asset._id.toString();
        const date = record.date;
        const cacheKey = `${assetId}-${moment(date).format('YYYY-MM-DD')}`;

        setChartIconLoading(prev => ({ ...prev, [cacheKey]: true }));

        assetSelectedRef.current = asset;
        setSelectedChartDate(date);
        setModalVisibleRef(true);
        setModalLoading(true);
        setChartDataReady(false);
        setModalKey(prevKey => prevKey + 1);

        try {
            const result = await ensureRefuelDataInCache(asset, record);
            if (result && result.error === 'NO_DEVICE') {
                message.info('No tag device found for this asset');
                setModalLoading(false);
                setChartDataReady(false);
                return;
            }

            setSelectedChartCacheKey(result && result.cacheKey ? result.cacheKey : null);
            setModalLoading(false);
            setChartDataReady(true);
        } catch (error) {
            console.error('Error fetching chart data:', error);
            message.error('Failed to fetch chart data');
            setModalLoading(false);
            setChartDataReady(false);
        } finally {
            setChartIconLoading(prev => ({ ...prev, [cacheKey]: false }));
        }
    };

    const handleProcessedInfoClick = async (asset, record) => {
        if (!asset || !asset._id) {
            message.error('Asset information is missing');
            return;
        }

        const assetId = asset._id.toString();
        const date = record && record.date;
        const cacheKey = `${assetId}-${moment(date).format('YYYY-MM-DD')}`;

        setProcessedInfoIconLoading(prev => ({ ...prev, [cacheKey]: true }));
        setProcessedInfoLoading(true);
        setProcessedInfoModalVisible(true);
        setProcessedInfoContext(null);

        try {
            const result = await ensureRefuelDataInCache(asset, record);
            if (result && result.error === 'NO_DEVICE') {
                message.info('No tag device found for this asset');
                setProcessedInfoModalVisible(false);
                return;
            }

            const cached = result && result.data ? result.data : null;
            const rangeData = {
                data: (cached && cached.refuelData && cached.refuelData.deviceData) || [],
                processedData: (cached && cached.refuelData && cached.refuelData.deviceProcessedData) || {},
            };

            const summary = buildProcessedDataSummary(rangeData, true, (cached && cached.fuelSensorCalibrationList) || []);
            setProcessedInfoContext({
                asset,
                rangeData,
                summary,
                fuelSensorCalibrationList: (cached && cached.fuelSensorCalibrationList) || [],
            });
        } catch (error) {
            console.error('Error fetching processed summary data:', error);
            message.error('Failed to fetch processed data');
            setProcessedInfoModalVisible(false);
        } finally {
            setProcessedInfoLoading(false);
            setProcessedInfoIconLoading(prev => ({ ...prev, [cacheKey]: false }));
        }
    };

    const handleModalClose = () => {
        setModalVisibleRef(false);
        setModalLoading(false);
        setChartDataReady(false);
        assetSelectedRef.current = null;
        setSelectedChartDate(null);
        setSelectedChartCacheKey(null);
        setModalKey(prevKey => prevKey + 1);
        setChartIconLoading({});
    };

    const handleProcessedInfoModalClose = () => {
        setProcessedInfoModalVisible(false);
        setProcessedInfoLoading(false);
        setProcessedInfoContext(null);
        setProcessedInfoIconLoading({});
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        props.form.validateFields(async (err, values) => {
            if (err) {
                return;
            }

            try {
                validateForm(null, values, async (validationError) => {
                    if (validationError) {
                        message.error(validationError);
                        return;
                    }

                    if (!moment(values.periodFromTo[0]).isSameOrBefore(values.periodFromTo[1])) {
                        message.error("Period From couldn't come earlier than Period To!");
                        return;
                    }

                    const payload = {
                        location: values.location,
                        subLocation: values.subLocation,
                        asset: values.asset || null,
                        periodFrom: moment(values.periodFromTo[0]).startOf('day').valueOf(),
                        periodTo: moment(values.periodFromTo[1]).endOf('day').valueOf(),
                    };

                    setProcessedInfoModalVisible(false);
                    setProcessedInfoContext(null);
                    setSelectedChartCacheKey(null);
                    setReportLoading(true);
                    setIsDownloadEnabled(false);
                    try {
                        assetDataRef.current = new Map();
                        const response = await axios.post('/fuel/refuel-report-v2', payload);
                        const rows = Array.isArray(response && response.data) ? response.data : [];
                        const filteredRows = rows.filter(hasManualOrDetectedRefuel);
                        const enrichedRows = await enrichReportRowsWithAssetMeta(filteredRows);
                        // By default only include assets that have a fuel sensor installed.
                        // The switch (default off) opts in to including the rest.
                        const finalRows = includeAssetsWithoutFuelSensor
                            ? enrichedRows
                            : enrichedRows.filter((row) => row && row.hasFuelSensorInstalled);
                        setRefuelReportData(finalRows);
                    } catch (error) {
                        console.error('Error fetching refuel report v2:', error);
                        message.error('Failed to fetch report');
                        setRefuelReportData([]);
                    } finally {
                        setReportLoading(false);
                        setIsDownloadEnabled(true);
                    }
                });
            } catch (error) {
                message.error('Form validation failed');
            }
        });
    };

    const downloadReport = (reportData) => {
        if (!reportData || !reportData.length) {
            return;
        }

        const docs = [...reportData].filter(hasManualOrDetectedRefuel);
        const fields = [
            { label: 'Date', value: (row) => row.date && moment(row.date).format('DD-MM-YYYY') },
            { label: 'Asset', value: (row) => row.asset && row.asset.assetCode },
            { label: 'Owner Code', value: (row) => row.asset && row.asset.ownerCode },
            { label: 'Asset Model', value: (row) => row.assetModel && row.assetModel.name },
            { label: 'GPS (IoT) Installed', value: (row) => row.asset ? (row.hasGpsInstalled ? 'Yes' : 'No') : '-' },
            { label: 'Fuel Sensor Installed', value: (row) => row.asset ? (row.hasFuelSensorInstalled ? 'Yes' : 'No') : '-' },
            { label: 'Location', value: (row) => row.location && row.location.name },
            { label: 'SubLocation', value: (row) => row.subLocation && row.subLocation.name },
            { label: 'Refuel Entry', value: (row) => Number(row.quantity || 0).toFixed(2) },
            { label: 'Refuel Detected', value: (row) => Number(row.refuelDetected || 0).toFixed(2) },
            {
                label: 'Data Available (hrs.)', value: (row) => {
                    const dataFromTimestamp = row && row.dataFromTimestamp;
                    const dataToTimestamp = row && row.dataToTimestamp;
                    if (dataFromTimestamp && dataToTimestamp) {
                        return ((dataToTimestamp - dataFromTimestamp) / 1000 / 60 / 60).toFixed(2);
                    }
                    return 'NA';
                }
            },
            {
                label: 'Difference', value: (row) => {
                    const refuelDetected = Number(row.refuelDetected || 0);
                    const quantity = Number(row.quantity || 0);
                    return Number(refuelDetected - quantity).toFixed(2);
                }
            },
            { label: 'Possible Drain', value: (row) => Number(row.theftDetected || 0).toFixed(2) },
        ];

        const csv = json2csv.parse(docs, { fields });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `fuel-sensor-reconciliation-report-${new Date().getTime()}.csv`);
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => <div>{date && moment(date).format('DD-MM-YYYY')}</div>
        },
        {
            title: 'Asset',
            dataIndex: 'asset',
            key: 'asset',
            render: (_, record) => record && record.asset && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <Link to={`/asset/history-report/${record.asset._id}`} target="_blank" rel="noopener noreferrer">
                        {`${record.asset.assetCode || '-'} / ${record.asset.altName || '-'} / ${record.asset.ownerCode || '-'}`}
                    </Link>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <Tooltip title={record.hasGpsInstalled ? 'GPS (IoT) installed' : 'GPS (IoT) not installed'}>
                            <Icon
                                type="environment"
                                style={{ color: record.hasGpsInstalled ? '#fa8c16' : '#bfbfbf' }}
                            />
                        </Tooltip>
                        <Tooltip title={record.hasFuelSensorInstalled ? 'Fuel sensor installed' : 'Fuel sensor not installed'}>
                            <Icon
                                type="dashboard"
                                style={{ color: record.hasFuelSensorInstalled ? '#722ed1' : '#bfbfbf' }}
                            />
                        </Tooltip>
                    </span>
                </span>
            )
        },
        {
            title: 'Asset Model',
            dataIndex: 'assetModel',
            key: 'assetModel',
            width: 180,
            render: (_, record) => record && record.assetModel && record.assetModel.name
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            render: (_, record) => record && record.location && record.location.name
        },
        {
            title: 'Sub Location',
            dataIndex: 'subLocation',
            key: 'subLocation',
            render: (_, record) => record && record.subLocation && record.subLocation.name
        },
        {
            title: 'Refuel Entry',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (quantity, record) => (
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        handleRefuelDetailsClick(record);
                    }}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        margin: 0,
                        color: '#1890ff',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                    }}
                >
                    {Number(quantity || 0).toFixed(2)}
                </button>
            )
        },
        {
            title: <span>Refuel Detected</span>,
            dataIndex: 'refuelDetected',
            key: 'refuelDetected',
            width: 220,
            render: (value, record) => {
                if (isNaN(Number(value))) {
                    return value;
                }

                const refuelDetected = Number(value || 0);
                const quantity = Number(record.quantity || 0);
                const difference = Math.abs(refuelDetected - quantity);

                let color = 'black';
                if (difference >= 10) {
                    color = 'red';
                } else if (difference >= 5) {
                    color = 'orange';
                }

                const loadingKey = `${record.asset && record.asset._id}-${moment(record.date).format('YYYY-MM-DD')}`;
                const isLoading = refuelClickLoading[loadingKey];
                const isChartLoading = chartIconLoading[loadingKey];
                const isProcessedInfoLoading = processedInfoIconLoading[loadingKey];

                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <button
                            type="button"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                margin: 0,
                                color,
                                textDecoration: 'underline',
                                cursor: 'pointer'
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleRefuelClick(record.asset, record);
                            }}
                            disabled={isLoading}
                        >
                            {isLoading
                                ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <div style={{
                                            width: '12px',
                                            height: '12px',
                                            border: '2px solid #f3f3f3',
                                            borderTop: '2px solid #3498db',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }} />
                                        Loading...
                                    </span>
                                )
                                : Number(refuelDetected).toFixed(2)
                            }
                        </button>

                        <button
                            type="button"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                margin: '0 0 0 5px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                            disabled={isChartLoading}
                            onClick={(e) => {
                                e.preventDefault();
                                handleChartClick(record.asset, record);
                            }}
                        >
                            {isChartLoading ? (
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid #f3f3f3',
                                    borderTop: '2px solid #3498db',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    display: 'inline-block'
                                }} />
                            ) : (
                                <img
                                    src={chartIcon}
                                    alt="Click to view chart"
                                    title="Click to view chart"
                                    style={{ width: '16px', height: '16px' }}
                                />
                            )}
                        </button>

                        <button
                            type="button"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                margin: '0 0 0 5px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                            disabled={isProcessedInfoLoading}
                            onClick={(e) => {
                                e.preventDefault();
                                handleProcessedInfoClick(record.asset, record);
                            }}
                            title="View processed data summary"
                        >
                            {isProcessedInfoLoading ? (
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid #f3f3f3',
                                    borderTop: '2px solid #3498db',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    display: 'inline-block'
                                }} />
                            ) : (
                                <Icon type="info-circle" style={{ fontSize: '16px', color: '#1890ff' }} />
                            )}
                        </button>
                    </span>
                );
            }
        },
        {
            title: 'Data Available (hrs.)',
            dataIndex: 'dataAvailable',
            key: 'dataAvailable',
            render: (_, record) => {
                const dataFromTimestamp = record && record.dataFromTimestamp;
                const dataToTimestamp = record && record.dataToTimestamp;
                if (dataFromTimestamp && dataToTimestamp) {
                    return <div>{((dataToTimestamp - dataFromTimestamp) / 1000 / 60 / 60).toFixed(2)}</div>;
                }
                return <div>NA</div>;
            }
        },
        {
            title: 'Difference',
            dataIndex: 'fuel_difference',
            key: 'fuel_difference',
            render: (_, record) => {
                const refuelDetected = Number(record.refuelDetected || 0);
                const quantity = Number(record.quantity || 0);
                const signedDifference = refuelDetected - quantity;
                const absoluteDifference = Math.abs(signedDifference);

                const calibration = record.assetFuelSensorCalibrations || null;
                const refuelShortageTolerance = (calibration && calibration.refuelShortageTolerance) || 1;
                const fullTankCapacity = calibration && calibration.list && calibration.list.length > 0
                    ? calibration.list[calibration.list.length - 1].quantity
                    : 100;

                if (absoluteDifference > (refuelShortageTolerance / 100) * fullTankCapacity) {
                    return <span style={{ color: 'red' }}>{Number(signedDifference || 0).toFixed(2)}</span>;
                }
                return Number(signedDifference || 0).toFixed(2);
            }
        },
        {
            title: 'Possible Drain',
            dataIndex: 'theftDetected',
            key: 'theftDetected',
            render: (_, record) => Number((record && record.theftDetected) || 0).toFixed(2)
        }
    ];

    const tableData = reportRows.length > 0
        ? reportRows.concat((() => {
            let totalRefuelDetected = 0;
            let totalTheftDetected = 0;
            let totalQuantity = 0;

            for (const row of reportRows) {
                totalRefuelDetected += row.refuelDetected && !isNaN(Number(row.refuelDetected)) ? Number(row.refuelDetected) : 0;
                totalTheftDetected += row.theftDetected && !isNaN(Number(row.theftDetected)) ? Number(row.theftDetected) : 0;
                totalQuantity += row.quantity && !isNaN(Number(row.quantity)) ? Number(row.quantity) : 0;
            }

            return {
                date: '',
                asset: null,
                ownerCode: '',
                assetModel: null,
                location: null,
                subLocation: { name: 'Total' },
                hasGpsInstalled: false,
                hasFuelSensorInstalled: false,
                quantity: totalQuantity,
                refuelDetected: totalRefuelDetected,
                theftDetected: totalTheftDetected,
                fuel_difference: '-',
            };
        })())
        : [];

    return (
        <Spin spinning={reportLoading}>
            <Card className="gx-card-metrics" title="Fuel Sensor Reconciliation Report">
                <Form onSubmit={handleSubmit} className="ant-advanced-search-form" layout="inline" labelAlign="left">
                    <Row>
                        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                            <Card>
                                <Form.Item key="location" label="Location" style={{ width: '100%' }}>
                                    {getFieldDecorator('location', {
                                        rules: [{ required: selectionMode === 'location', message: 'Location is required!' }],
                                    })(
                                        <Select
                                            style={{ width: '100%' }}
                                            showSearch
                                            showArrow
                                            disabled={selectionMode === 'asset'}
                                            onChange={() => setSelectionMode('location')}
                                            filterOption={(input, option) =>
                                                (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
                                            }
                                            placeholder="Select Location"
                                        >
                                            <Select.Option key="AllLocation" value="all">All</Select.Option>
                                            {((props.authUser && props.authUser.locations) || []).map(item => (
                                                <Select.Option key={item._id} value={item._id}>{item.name}</Select.Option>
                                            ))}
                                        </Select>
                                    )}
                                </Form.Item>

                                <Form.Item key="subLocation" label="Sub Location" style={{ width: '100%' }}>
                                    {getFieldDecorator('subLocation', {
                                        rules: [{ required: selectionMode === 'location', message: 'Sub Location is required!' }],
                                    })(
                                        <Select
                                            style={{ width: '100%' }}
                                            showSearch
                                            showArrow
                                            disabled={selectionMode === 'asset'}
                                            onChange={() => setSelectionMode('location')}
                                            filterOption={(input, option) =>
                                                (option.props.children + '').toLowerCase().indexOf((input + '').toLowerCase()) >= 0
                                            }
                                            placeholder="Select Sub Location"
                                        >
                                            <Select.Option key="AllSubLocation" value="all">All</Select.Option>
                                            {((props.authUser && props.authUser.clients) || []).map(item => (
                                                <Select.Option key={item._id} value={item._id}>{item.name}</Select.Option>
                                            ))}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Card>
                        </Col>

                        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
                            <Card>
                                <Form.Item key="asset" label="Asset" style={{ width: '100%' }}>
                                    {getFieldDecorator('asset', {
                                        rules: [{ required: selectionMode === 'asset', message: 'Asset is required!' }],
                                    })(
                                        <AssetSearch
                                            styleName="gx-lt-icon-search-bar-lg"
                                            placeholder="Search any Assets..."
                                            onChange={props.searchGlobalAssetData}
                                            assetData={props.assetData && props.assetData.filter((ast) => ast.assetModel)}
                                            onSelect={(value) => {
                                                props.form.setFieldsValue({ asset: value });
                                                setSelectionMode('asset');
                                                props.form.setFieldsValue({ location: undefined, subLocation: undefined });
                                            }}
                                            disabled={selectionMode === 'location'}
                                            authUser={props.authUser}
                                            loading={props.assetSearchLoading}
                                            multiple={true}
                                        />
                                    )}
                                </Form.Item>
                                <Form.Item key="includeNoFuelSensor" style={{ width: '100%', marginBottom: 0 }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Switch
                                            size="small"
                                            checked={includeAssetsWithoutFuelSensor}
                                            onChange={setIncludeAssetsWithoutFuelSensor}
                                        />
                                        <span>Include assets without fuel sensor</span>
                                        <Tooltip title="When off, only assets that have a fuel sensor installed are included in the report.">
                                            <Icon type="info-circle" style={{ color: '#bfbfbf' }} />
                                        </Tooltip>
                                    </span>
                                </Form.Item>
                            </Card>
                        </Col>

                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Form.Item key="periodFromTo" label="Select Period" style={{ width: '100%' }}>
                                {getFieldDecorator('periodFromTo', {
                                    rules: [
                                        { required: true, message: 'Period selection is required!' },
                                        {
                                            validator: (_, value, callback) => {
                                                if (!value || value.length !== 2) {
                                                    callback();
                                                    return;
                                                }
                                                if (!selectionMode) {
                                                    callback('Please select either Location/Sub-Location or Asset first');
                                                    return;
                                                }
                                                callback();
                                            }
                                        }
                                    ],
                                    initialValue: null,
                                })(<RangePicker style={{ width: '100%' }} />)}
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Form.Item style={{ marginTop: '25px' }}>
                                <span style={{ display: 'flex', flexDirection: 'row' }}>
                                    <Button type="primary" htmlType="submit" loading={reportLoading} disabled={!isDownloadEnabled}>
                                        Generate Report
                                    </Button>
                                    <Button
                                        disabled={!reportRows || !reportRows.length || !isDownloadEnabled}
                                        type="default"
                                        htmlType="button"
                                        loading={reportLoading || !isDownloadEnabled}
                                        icon="download"
                                        onClick={() => downloadReport(reportRows)}
                                    >
                                        Download
                                    </Button>

                                    <Button
                                        type="ghost"
                                        onClick={() => {
                                            props.form.resetFields();
                                            setRefuelReportData([]);
                                            setSelectionMode(null);
                                            setProcessedInfoModalVisible(false);
                                            setProcessedInfoContext(null);
                                            setSelectedChartCacheKey(null);
                                            assetDataRef.current = new Map();
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </span>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                {refuelReportData ? (
                    <Table
                        dataSource={tableData}
                        columns={columns}
                        pagination={false}
                        bordered
                        scroll={{ x: true }}
                        rowKey={(record) => `${record.asset && record.asset._id}-${record.date || 'total'}`}
                    />
                ) : reportLoading ? (
                    <Spin />
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Card>

            {assetSelectedRef.current && modalVisibleRef && assetDataRef.current && selectedChartCacheKey
                && assetDataRef.current.get(selectedChartCacheKey)
                && (
                    <Modal
                        key={modalKey}
                        visible={modalVisibleRef}
                        onCancel={handleModalClose}
                        footer={null}
                        width="90%"
                        style={{ top: 20 }}
                        bodyStyle={{ height: 'calc(90vh - 108px)' }}
                    >
                        {modalLoading ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                fontSize: '16px',
                                color: '#666'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ marginBottom: '10px' }}>Loading chart data...</div>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '4px solid #f3f3f3',
                                        borderTop: '4px solid #3498db',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                        margin: '0 auto'
                                    }} />
                                </div>
                            </div>
                        ) : chartDataReady && assetDataRef.current ? (
                            (() => {
                                const assetData = assetDataRef.current.get(selectedChartCacheKey);
                                const chartData = assetData && assetData.refuelData && assetData.refuelData.deviceData;

                                if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
                                    return (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '100%',
                                            fontSize: '16px',
                                            color: '#666'
                                        }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ marginBottom: '10px' }}>No chart data available</div>
                                                <div style={{ color: '#999', fontSize: '14px' }}>
                                                    No device data found for the selected date and asset.
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <RangeDataChart
                                        data={chartData}
                                        hasFuelSensorAttached={true}
                                        data_gaps={(assetData && assetData.refuelData && assetData.refuelData.deviceProcessedData && assetData.refuelData.deviceProcessedData.data_gaps) || []}
                                        consumedData={(assetData && assetData.refuelData && assetData.refuelData.deviceProcessedData && assetData.refuelData.deviceProcessedData.newConsumed) || null}
                                        fuelSensorCalibrationList={(assetData && assetData.fuelSensorCalibrationList) || []}
                                        title={`Data Chart for ${assetSelectedRef.current.assetCode || '-'} / ${assetSelectedRef.current.altName || '-'} / ${assetSelectedRef.current.ownerCode || '-'}`}
                                        skipInitialLoading={true}
                                    />
                                );
                            })()
                        ) : (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                                fontSize: '16px',
                                color: '#666'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ marginBottom: '10px' }}>Chart data not ready</div>
                                    <div style={{ color: '#999', fontSize: '14px' }}>
                                        Please wait for the data to load or try refreshing.
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal>
                )}

            <Modal
                title={`Processed Data Summary${processedInfoContext && processedInfoContext.asset ? ` - ${processedInfoContext.asset.assetCode || '-'} / ${processedInfoContext.asset.altName || '-'} / ${processedInfoContext.asset.ownerCode || '-'}` : ''}`}
                visible={processedInfoModalVisible}
                onCancel={handleProcessedInfoModalClose}
                footer={null}
                width={500}
            >
                {processedInfoLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Spin />
                    </div>
                ) : processedInfoContext ? (
                    <ProcessedDataSummary
                        rangeData={processedInfoContext.rangeData}
                        summary={processedInfoContext.summary}
                        hasFuelSensorAttached={true}
                        fuelSensorCalibrationList={processedInfoContext.fuelSensorCalibrationList || []}
                    />
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Modal>

            <Modal
                title="Refuel Details"
                visible={refuelDetailsModalVisible}
                onCancel={handleRefuelDetailsModalClose}
                footer={null}
                width={800}
            >
                <Table
                    dataSource={selectedRefuelDetails || []}
                    columns={[
                        {
                            title: 'Voucher No',
                            dataIndex: 'voucherNo',
                            key: 'voucherNo',
                            render: (voucherNo, record) => {
                                const hasEditPermission = props.authUser && props.authUser.roles
                                    && props.authUser.roles.map(r => r.access)
                                        .flat(1).filter(a => a && a.name === 'Fuel')[0]
                                    && props.authUser.roles.map(r => r.access)
                                        .flat(1).filter(a => a && a.name === 'Fuel')[0].permissions
                                    && props.authUser.roles.map(r => r.access)
                                        .flat(1).filter(a => a && a.name === 'Fuel')[0].permissions.includes('u');

                                return hasEditPermission ? (
                                    <Link to={`/fuel/edit/${record._id}`} target="_blank" rel="noopener noreferrer">
                                        {voucherNo}
                                    </Link>
                                ) : (
                                    <Link to={`/fuel/view/${record._id}`} target="_blank" rel="noopener noreferrer">
                                        {voucherNo}
                                    </Link>
                                );
                            }
                        },
                        {
                            title: 'Date',
                            dataIndex: 'date',
                            key: 'date',
                            render: (date) => moment(date).format('DD-MM-YYYY HH:mm:ss')
                        },
                        {
                            title: 'Quantity',
                            dataIndex: 'quantity',
                            key: 'quantity',
                            render: (qty) => Number(qty || 0).toFixed(2)
                        },
                        {
                            title: 'Meters',
                            dataIndex: 'meters',
                            key: 'meters',
                            render: (meters) => Array.isArray(meters) ? meters.map(m => `${m.reading} ${m.name}`).join(', ') : '-'
                        },
                        {
                            title: 'Photos',
                            dataIndex: 'photos',
                            key: 'photos',
                            render: (photos, record) => {
                                if (!photos || !Array.isArray(photos) || !photos.length) return '-';
                                return (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {photos.map((p, index) => {
                                            if (!p || !p.url) return null;
                                            const globalIndex = allRefuelPhotos.findIndex(
                                                (item) => item.record === record && item.url === p.url && item.name === p.name
                                            );
                                            return (
                                                <img
                                                    key={index}
                                                    src={p.url}
                                                    alt={p.name}
                                                    title={p.name}
                                                    onClick={() => globalIndex >= 0 && openPhotoPreview(globalIndex)}
                                                    style={{
                                                        width: 44,
                                                        height: 44,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        border: '1px solid #d9d9d9',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            }
                        }
                    ]}
                    pagination={false}
                    size="small"
                    rowKey={(record, index) => (record && record._id ? record._id : index)}
                />
            </Modal>

            <ImagePreviewModal
                visible={photoPreview.visible}
                photos={allRefuelPhotos}
                initialIndex={photoPreview.index}
                onClose={closePhotoPreview}
                renderDetails={(photo) => {
                    const rec = (photo && photo.record) || {};
                    const metersText = Array.isArray(rec.meters) ? rec.meters.map(m => `${m.reading} ${m.name}`).join(', ') : '-';
                    const labelStyle = { padding: '4px 8px', color: '#8c8c8c', width: 110, verticalAlign: 'top' };
                    const valueStyle = { padding: '4px 8px', color: '#262626', wordBreak: 'break-word' };
                    return (
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                            <tbody>
                                <tr>
                                    <td style={labelStyle}>Voucher No</td>
                                    <td style={valueStyle}>{rec.voucherNo || '-'}</td>
                                </tr>
                                <tr>
                                    <td style={labelStyle}>Date</td>
                                    <td style={valueStyle}>{rec.date ? moment(rec.date).format('DD-MM-YYYY HH:mm:ss') : '-'}</td>
                                </tr>
                                <tr>
                                    <td style={labelStyle}>Quantity</td>
                                    <td style={valueStyle}>{Number(rec.quantity || 0).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={labelStyle}>Meters</td>
                                    <td style={valueStyle}>{metersText}</td>
                                </tr>
                            </tbody>
                        </table>
                    );
                }}
            />

            <Modal
                title="Refuel Events"
                visible={refuelEventsModalVisible}
                onCancel={handleRefuelEventsModalClose}
                footer={null}
                width={600}
            >
                <Table
                    dataSource={selectedRefuelEvents}
                    columns={[
                        {
                            title: '~ Time',
                            dataIndex: 'timestamp',
                            key: 'timestamp',
                            render: (timestamp) => moment(timestamp).format('DD-MM-YYYY hh:mm:ss A')
                        },
                        {
                            title: '~ Quantity',
                            dataIndex: 'refuelAmount',
                            key: 'refuelAmount',
                            render: (refuelAmount) => Math.abs(Number(refuelAmount || 0)).toFixed(2)
                        }
                    ]}
                    pagination={false}
                    size="small"
                    rowKey={(record, index) => index}
                />
            </Modal>
        </Spin>
    );
}

const RefuelReport = Form.create({ name: 'refuel-report' })(Refuel);
export default connect(mapStateToProps, mapDispatchToProps)(RefuelReport);
